import type { Rng } from './rng.js';

/** r: 2..14 (14 = ace), s: 0..3 (spades, hearts, diamonds, clubs) */
export interface Card {
  r: number;
  s: number;
}

export const SUIT_SYM = ['♠', '♥', '♦', '♣'] as const;
export const SUIT_CLS = ['', 'red', 'blue', 'green'] as const;
export const RANK_SYM: Record<number, string> = {
  2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'B', 12: 'D', 13: 'K', 14: 'A',
};
export const CAT_NAMES = [
  'Hohe Karte', 'Ein Paar', 'Zwei Paare', 'Drilling', 'Straße', 'Flush', 'Full House', 'Vierling', 'Straight Flush', 'Royal Flush',
] as const;

export function newDeck(rng: Rng): Card[] {
  const d: Card[] = [];
  for (let s = 0; s < 4; s++) for (let r = 2; r <= 14; r++) d.push({ r, s });
  for (let i = d.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [d[i], d[j]] = [d[j]!, d[i]!];
  }
  return d;
}

export function evaluate5(c: readonly Card[]): number {
  const rs = [c[0]!.r, c[1]!.r, c[2]!.r, c[3]!.r, c[4]!.r].sort((a, b) => b - a);
  const flush = c[0]!.s === c[1]!.s && c[0]!.s === c[2]!.s && c[0]!.s === c[3]!.s && c[0]!.s === c[4]!.s;
  const cnt: Record<number, number> = {};
  for (const r of rs) cnt[r] = (cnt[r] ?? 0) + 1;
  const groups = Object.keys(cnt)
    .map((r) => ({ r: +r, n: cnt[+r]! }))
    .sort((a, b) => b.n - a.n || b.r - a.r);
  let straight = 0;
  if (groups.length === 5) {
    if (rs[0]! - rs[4]! === 4) straight = rs[0]!;
    else if (rs[0] === 14 && rs[1] === 5 && rs[4] === 2) straight = 5;
  }
  let cat: number;
  let kick: number[];
  if (straight && flush) {
    cat = straight === 14 ? 9 : 8;
    kick = [straight];
  } else if (groups[0]!.n === 4) {
    cat = 7;
    kick = [groups[0]!.r, groups[1]!.r];
  } else if (groups[0]!.n === 3 && groups[1] && groups[1].n === 2) {
    cat = 6;
    kick = [groups[0]!.r, groups[1].r];
  } else if (flush) {
    cat = 5;
    kick = rs;
  } else if (straight) {
    cat = 4;
    kick = [straight];
  } else if (groups[0]!.n === 3) {
    cat = 3;
    kick = [groups[0]!.r, groups[1]!.r, groups[2]!.r];
  } else if (groups[0]!.n === 2 && groups[1] && groups[1].n === 2) {
    cat = 2;
    kick = [groups[0]!.r, groups[1].r, groups[2]!.r];
  } else if (groups[0]!.n === 2) {
    cat = 1;
    kick = [groups[0]!.r, groups[1]!.r, groups[2]!.r, groups[3]!.r];
  } else {
    cat = 0;
    kick = rs;
  }
  let score = cat;
  for (let i = 0; i < 5; i++) score = score * 15 + (kick[i] ?? 0);
  return score;
}

const COMBOS7: number[][] = [];
const COMBOS6: number[][] = [];
for (let a = 0; a < 7; a++)
  for (let b = a + 1; b < 7; b++)
    for (let c = b + 1; c < 7; c++)
      for (let d = c + 1; d < 7; d++)
        for (let e = d + 1; e < 7; e++) COMBOS7.push([a, b, c, d, e]);
for (let a = 0; a < 6; a++)
  for (let b = a + 1; b < 6; b++)
    for (let c = b + 1; c < 6; c++)
      for (let d = c + 1; d < 6; d++)
        for (let e = d + 1; e < 6; e++) COMBOS6.push([a, b, c, d, e]);

export interface BestHand {
  score: number;
  cards: Card[];
}

export function bestHand(cards: Card[]): BestHand {
  if (cards.length <= 5) {
    if (cards.length < 5) return { score: 0, cards };
    return { score: evaluate5(cards), cards };
  }
  const combos = cards.length === 7 ? COMBOS7 : COMBOS6;
  let best = -1;
  let bc: Card[] = [];
  for (const k of combos) {
    const five = [cards[k[0]!]!, cards[k[1]!]!, cards[k[2]!]!, cards[k[3]!]!, cards[k[4]!]!];
    const s = evaluate5(five);
    if (s > best) {
      best = s;
      bc = five;
    }
  }
  return { score: best, cards: bc };
}

export const catOf = (score: number): number => Math.floor(score / Math.pow(15, 5));
export const handName = (score: number): string => CAT_NAMES[catOf(score)] ?? CAT_NAMES[0];

/** Monte-Carlo equity. `rand` defaults to Math.random (client display / AI). */
export function equity(hole: Card[], board: Card[], nOpp: number, iters: number, rand: () => number = Math.random): number {
  const used = new Set([...hole, ...board].map((c) => c.r * 4 + c.s));
  const pool: Card[] = [];
  for (let s = 0; s < 4; s++)
    for (let r = 2; r <= 14; r++) if (!used.has(r * 4 + s)) pool.push({ r, s });
  const need = 5 - board.length;
  let wins = 0;
  for (let i = 0; i < iters; i++) {
    const take = need + nOpp * 2;
    for (let k = 0; k < take; k++) {
      const j = k + Math.floor(rand() * (pool.length - k));
      [pool[k], pool[j]] = [pool[j]!, pool[k]!];
    }
    const full = board.concat(pool.slice(0, need));
    const my = bestHand(hole.concat(full)).score;
    let best = true;
    let tie = 1;
    for (let o = 0; o < nOpp; o++) {
      const os = bestHand([pool[need + o * 2]!, pool[need + o * 2 + 1]!].concat(full)).score;
      if (os > my) {
        best = false;
        break;
      }
      if (os === my) tie++;
    }
    if (best) wins += 1 / tie;
  }
  return wins / iters;
}
